import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Scale,
  Shirt,
  Sparkles,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/arm-lift-surgery-mumbai";

const ARM_LIFT_FAQS = [
  {
    question: "Will exercise fix loose upper arm skin?",
    answer:
      "No. Exercise builds the tricep muscle beneath the skin but does not address skin laxity itself. Patients who work out consistently and still have loose arm skin have a skin problem, not a muscle problem. Brachioplasty removes the excess skin that exercise cannot correct.",
  },
  {
    question: "How visible is the arm lift scar?",
    answer:
      "The scar runs along the inner surface of the upper arm from the armpit to just above the elbow. When the arms are at rest by the side, the scar is on the inner surface and not visible from the front or back. It fades from pink and slightly raised to a thin pale line over 12 to 18 months. The trade-off between the scar and the correction of significant skin excess is consistently viewed positively by patients after the scar has matured.",
  },
  {
    question: "Can arm lift be combined with liposuction?",
    answer:
      "Yes, and this is the standard approach for most patients. Liposuction is performed first to remove the fatty component of arm fullness, then skin excision addresses the laxity. The combination produces a more completely contoured result than skin removal alone.",
  },
  {
    question: "When can I return to exercise after brachioplasty?",
    answer:
      "Light arm activity and walking resume within 2 weeks. Gym activity involving upper body exercise, lifting, and overhead movements resumes after 6 weeks when the incision has healed and the deep tissue closure has matured sufficiently to tolerate the tension.",
  },
  {
    question: "Are arm lift results permanent?",
    answer:
      "The skin removed does not return. The result is permanent provided weight remains stable. Significant weight gain after arm lift adds volume to the remaining tissue and can stretch the skin that was tightened. Aging continues to affect skin quality after surgery as it does everywhere else on the body, but the structural improvement from the surgery is maintained.",
  },
  {
    question: "Can arm lift be performed alongside other body contouring procedures?",
    answer:
      "Yes. Arm lift is frequently combined with tummy tuck, thigh lift, breast lift, and other procedures as part of a comprehensive post-weight loss body contouring plan. Combining procedures reduces the total number of surgeries, anesthesia exposures, and recovery periods. The combination is planned based on what the patient's health safely allows in a single surgery rather than aesthetic preference alone.",
  },
  {
    question: "Is there a non-surgical alternative to arm lift?",
    answer:
      "Non-surgical treatments including radiofrequency, ultrasound, and energy-based devices can produce modest skin tightening in patients with mild, early laxity. They do not produce results comparable to surgical skin removal in patients with moderate or significant excess. For patients with established skin hanging below the arm, non-surgical treatments produce insufficient correction to be a meaningful alternative.",
  },
  {
    question: "What are the risks of arm lift surgery?",
    answer:
      "Swelling, bruising, and temporary numbness along the inner arm are expected during recovery. Wound healing delays, scar widening, seroma, and infection are possible but uncommon. The inner arm scar is the most common source of patient concern after surgery. Scar quality depends on surgical technique, tension management at closure, and patient healing factors including smoking status and metabolic health. Choosing an experienced surgeon and following post-operative care instructions significantly reduces complication risk.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Arm lift",
      alternateName: ["Brachioplasty", "Upper arm lift"],
      description:
        "Surgical excision of excess upper arm skin and fat, often with liposuction, to improve arm contour after weight loss, aging, or skin laxity.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Upper arm" },
      howPerformed:
        "Marking with arms extended and at rest; liposuction when indicated; excision of redundant skin along medial upper arm; layered closure; compression garment; general anesthesia or deep sedation; bilateral; 2–3 hours; typically same-day discharge.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical body contouring" },
        { "@type": "PropertyValue", name: "Common combinations", value: "Liposuction, post–weight loss body contouring" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: ARM_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ArmLiftJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Arm Lift", href: "/arm-lift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Arm Lift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Brachioplasty by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Arm Lift" icon="💪" size="hero"  imageSrc={HERO.armLift} imageAlt="Arm lift surgery"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Arm Lift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              An arm lift, clinically called brachioplasty, removes excess skin and fat from the upper arms to produce a toned, firm,
              and well-contoured arm appearance. It corrects the loose, hanging skin that develops after significant weight loss, with
              aging, or from genetics, and does not reduce through exercise. Building the tricep muscle underneath loose skin improves
              arm strength but does not address the skin itself. Surgery is the only treatment that removes excess skin.
            </p>
            <p>
              The critical distinction in arm contouring is whether the problem is fat, skin, or both. Fat-only arm fullness in a
              patient with good skin elasticity responds well to liposuction alone. Skin excess, with or without fat, requires
              brachioplasty. Most patients presenting for arm contouring have a combination of both, making arm lift with simultaneous
              liposuction the most common approach. Dr. Jain assesses which component is dominant during consultation to determine the
              appropriate technique.
            </p>
            <p>
              Dr. Vicky Jain performs brachioplasty as a standalone procedure and in combination with tummy tuck, thigh lift,
              and other body contouring procedures for patients undergoing comprehensive post-weight loss body reshaping. The procedure
              takes 2 to 3 hours and most patients return to light activity within 2 weeks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Brachioplasty</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The trade-off in arm lift surgery is explicit and patients need to understand it before committing to the procedure:
                the scar runs along the inner surface of the upper arm from the armpit to near the elbow. It is a long scar. It fades
                significantly over 12 to 18 months and sits on the inner arm surface where it is not visible when the arms are at rest,
                but it is present. For patients with significant arm skin excess, this trade-off is consistently worth it. For patients
                with mild excess, the scar-to-result ratio requires honest discussion during consultation.
              </p>
              <p>
                Dr. Jain marks the planned incision, the skin to be removed, and the extent of any liposuction with the patient
                standing before surgery. The amount of skin removed determines the tightness of the final result and the position of the
                incision. Removing too little leaves residual laxity. Removing too much creates excessive tension on the closure, which
                widens the scar and can cause wound healing problems. The calibration between these two is one of the most important
                technical decisions in the procedure.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Loose Hanging Upper Arm Skin",
      icon: Layers,
      copy: "Skin that drapes below the arm when it is raised, creating the characteristic bat wing appearance. This skin excess cannot be reduced by exercise, diet, or non-surgical treatments. Surgical removal is the only effective correction.",
    },
    {
      title: "Post-Weight Loss Arm Skin",
      icon: Scale,
      copy: "Significant or rapid weight loss removes the fat volume that was supporting the arm skin. The excess skin cannot retract adequately and hangs from the upper arm. The degree of excess is proportional to how much weight was lost and how quickly.",
    },
    {
      title: "Age-Related Skin Laxity",
      icon: Sparkles,
      copy: "Upper arm skin loses elasticity with age and the underlying fat redistributes. The result is soft, loose skin that creates fullness without definition. Brachioplasty removes this skin and restores a toned arm contour.",
    },
    {
      title: "Upper Arm Fat",
      icon: Minimize2,
      copy: "When excess arm fat accompanies skin laxity, liposuction is performed alongside skin removal. Fat-only arm fullness without skin excess is treated with liposuction alone. The combination approach produces the most complete contouring result.",
    },
    {
      title: "Skin Excess After Bariatric Surgery",
      icon: Activity,
      copy: "Patients who have undergone bariatric procedures and lost substantial weight frequently develop significant arm skin excess that is among the most functionally limiting post-weight loss changes. Arm lift is commonly performed as part of a staged post-bariatric body contouring plan.",
    },
    {
      title: "Inability to Wear Sleeveless Clothing",
      icon: Shirt,
      copy: "Many patients describe avoiding sleeveless tops, dresses, and swimwear for years before seeking treatment. While this is an aesthetic rather than functional concern, its impact on daily clothing choices and confidence is consistent and significant.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Brachioplasty Corrects</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Mini Arm Lift",
    body:
      "A limited incision confined to the armpit area. Appropriate for patients with mild skin laxity concentrated near the axilla without significant skin excess extending down the inner arm. Produces minimal scarring but limited correction. Not appropriate as a substitute for standard brachioplasty in patients with moderate or significant skin excess along the arm length.",
  },
  {
    title: "Standard Arm Lift",
    body:
      "The most commonly performed technique. The incision runs along the inner surface of the upper arm from the armpit to just above the elbow. Allows removal of skin excess along the full length of the upper arm. Appropriate for patients with moderate to significant laxity. The scar is positioned on the inner arm surface where it is concealed when the arms are at rest by the side.",
  },
  {
    title: "Extended Arm Lift",
    body:
      "Extends the incision into the lateral chest wall to address skin excess that continues from the upper arm onto the side of the torso. Used for patients after massive weight loss where skin excess is not confined to the arm itself but extends into the axillary and lateral chest area.",
  },
  {
    title: "Arm Lift with Liposuction",
    body:
      "Liposuction is performed before skin removal in most arm lift procedures to remove the fatty component of arm fullness, reduce the volume of tissue being repositioned, and improve the final contour. The combination produces a more defined result than skin removal alone. Used as the default approach for patients with both skin excess and arm fat.",
  },
];

function TypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Arm Lift Surgery</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Arm Lift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 3 hours under general anesthesia or deep sedation. Dr. Jain marks the planned incision and the skin to
              be removed with the patient&apos;s arms extended and then at rest, confirming that the planned scar position sits on the
              inner arm surface in both positions.
            </p>
            <p>
              When liposuction is being performed alongside the lift, this is completed first. Removing the fat component before skin
              excision improves visualization of the remaining skin excess and allows a more accurate assessment of how much skin needs
              to be removed. The skin is then excised, the deep tissue is closed in layers to take tension off the skin closure, and
              the skin is sutured with fine sutures.
            </p>
            <p>
              A compression garment covering the upper arms is applied immediately after surgery. Both arms are treated in the same
              procedure. Most patients go home the same day. The arms are kept elevated when resting during the first week to reduce
              swelling.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling, bruising, and tightness along the inner arms. Compression garment worn continuously. Arms kept elevated when resting. Most patients are mobile but limit arm use to avoid tension on the incision.",
      icon: BedDouble,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Most patients resume light daily activity. Desk work is manageable. The arms look noticeably more toned even through residual swelling. Compression garment continues during the day.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Return to normal routine for most patients. The inner arm scar is healing. Avoid lifting heavy objects or overhead arm activity.",
      icon: PersonStanding,
    },
    {
      period: "Weeks 6 to 8 and Beyond",
      description:
        "Full activity including gym resumes. Swelling fully resolved. The arm contour is at its final result. Scar maturation continues over 12 to 18 months.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "Skin Removal",
    lift: "Yes. Excess skin is surgically excised.",
    lipo: "No. Skin must contract on its own after fat removal.",
  },
  {
    label: "Fat Removal",
    lift: "Yes, combined with liposuction in most cases.",
    lipo: "Yes, the primary function.",
  },
  {
    label: "Scar",
    lift: "Long scar along inner upper arm, hidden at rest.",
    lipo: "Small access points only, essentially invisible.",
  },
  {
    label: "Best For",
    lift: "Patients with skin excess, with or without arm fat.",
    lipo: "Patients with arm fat and good skin elasticity who do not have skin excess.",
  },
  {
    label: "What Happens if Wrong Procedure is Chosen",
    lift: "Not applicable.",
    lipo: "Fat reduces but skin remains loose, worsening the laxity appearance.",
  },
];

function ArmLiftVsLipoSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="arm-lift-vs-lipo-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Arm Lift vs. Arm Liposuction: Which Do You Need?
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[640px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[22%]" />
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Arm Lift
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Arm Liposuction
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.lift}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.lipo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed text-sm md:text-base">
          The most common error in arm contouring is performing liposuction on a patient who needs an arm lift. Removing fat from an
          arm with poor skin elasticity and existing laxity leaves the skin with less volume supporting it, which worsens the hanging
          appearance. Skin elasticity assessment before any arm contouring procedure determines which approach is appropriate.
        </p>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Arm Lift Surgery?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults with upper arm skin excess that does not improve with exercise, are at a stable weight, and
              are in good general health. The most common presentations are patients after significant weight loss, patients in their
              40s and beyond with age-related laxity, and patients who have noticed progressive arm skin loosening over several years
              that has now reached a degree they find limiting. There is no upper age limit. Patients in their 60s and 70s with good
              general health are appropriate candidates.
            </p>
            <p>
              Stable weight before surgery matters significantly. Patients who are still losing weight should complete their weight
              loss before arm lift because continued weight loss after surgery will create new skin excess in the same area that was
              just corrected. Non-smokers or patients willing to stop smoking at least 4 weeks before surgery have better wound healing
              outcomes. The inner arm incision is in a location that is under moderate tension and heals well in healthy non-smokers.
              Patients with diabetes or conditions that affect wound healing are assessed carefully before surgery as these conditions
              increase the risk of delayed healing and widened scars.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Arm Lift" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {ARM_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`arm-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Arm Lift Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your upper arm skin laxity, fat distribution, and skin elasticity to determine
          whether arm lift, liposuction, or both is appropriate for your anatomy. You leave with a clear understanding of the scar
          trade-off, the expected result, and what recovery involves at each stage.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Tummy Tuck",
      description: "Address abdominal skin excess alongside arm correction as part of post-weight loss body contouring.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Fat removal from arms and other areas for patients with fat-only fullness and good skin elasticity.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Complete post-pregnancy body restoration addressing arms, abdomen, and breasts together.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ArmLiftPage() {
  return (
    <>
      <SEO
        title="Arm Lift Surgery | Brachioplasty by Dr. Vicky Jain"
        description="Loose upper arm skin that exercise cannot fix needs surgical correction. Dr. Vicky Jain performs brachioplasty with lasting results. Book your consultation."
        canonical="/arm-lift-surgery-mumbai"
      />
      <ArmLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <TypesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <ArmLiftVsLipoSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
